import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { RegisterStatusProvider } from './context/RegisterStatus';
import { PropertyProvider } from './context/PropertyContext';
import Login from './pages/Login';
import Layout from './components/Layout';
import Today from './pages/Today';
import Arrivals from './pages/Arrivals';
import Payments from './pages/Payments';
import Holds from './pages/Holds';
import Register from './pages/Register';
import Staff from './pages/Staff';
import Scanner from './pages/Scanner';
import Calendar from './pages/Calendar';
import Settings from './pages/Settings';
import AuditLog from './pages/AuditLog';
import PrintArrivals from './pages/PrintArrivals';
import AuthCallback from './pages/AuthCallback';
import './App.css';

function Guard({ roles, children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="p-8 text-[#333]/70">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  const location = useLocation();

  // Emergent OAuth returns to us with a session_id in the URL hash.
  // Process it BEFORE any other auth check runs.
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  if (user === null) return <div className="app-bg p-8 text-[#333]/70">Loading…</div>;
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/print/arrivals" element={<Guard roles={['front_desk', 'manager']}><PrintArrivals /></Guard>} />
      <Route element={<Guard><RegisterStatusProvider><Layout /></RegisterStatusProvider></Guard>}>
        <Route path="/" element={<Today />} />
        <Route path="/arrivals" element={<Guard roles={['front_desk', 'manager']}><Arrivals /></Guard>} />
        <Route path="/scanner" element={<Guard roles={['front_desk', 'manager']}><Scanner /></Guard>} />
        <Route path="/payments" element={<Guard roles={['finance', 'manager']}><Payments /></Guard>} />
        <Route path="/holds" element={<Guard roles={['finance', 'manager']}><Holds /></Guard>} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/register" element={<Guard roles={['manager']}><Register /></Guard>} />
        <Route path="/staff" element={<Guard roles={['manager']}><Staff /></Guard>} />
        <Route path="/audit" element={<Guard roles={['manager']}><AuditLog /></Guard>} />
        <Route path="/settings" element={<Guard roles={['manager']}><Settings /></Guard>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <PropertyProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </PropertyProvider>
      </BrowserRouter>
    </div>
  );
}
